import { useState } from "react";
import { FiCheck, FiMinus, FiZap, FiArrowRight } from "react-icons/fi";

const PLANS = [
  {
    key: "starter",
    name: "Starter",
    tagline: "Perfect for getting started",
    monthlyPrice: 29,
    yearlyPrice: 23,
    yearlyTotal: 276,
    cta: "Downgrade",
    color: "from-blue-500 to-indigo-500",
    limits: { chatbots: "2 Chatbots", messages: "10,000 msgs / mo" },
  },
  {
    key: "pro",
    name: "Pro",
    tagline: "Most popular for growing teams",
    monthlyPrice: 79,
    yearlyPrice: 63,
    yearlyTotal: 756,
    cta: "Current Plan",
    current: true,
    color: "from-primary to-indigo-500",
    limits: { chatbots: "10 Chatbots", messages: "100,000 msgs / mo" },
  },
  {
    key: "enterprise",
    name: "Enterprise",
    tagline: "Custom volume & SLA guarantees",
    monthlyPrice: null,
    cta: "Contact Sales",
    color: "from-gray-700 to-gray-900",
    limits: { chatbots: "Unlimited Chatbots", messages: "Unlimited messages" },
  },
];

/* ─ Feature rows: [label, starter, pro, enterprise] ─ */
const FEATURES = [
  { section: "Core Limits" },
  { label: "Chatbots",            starter: "2",         pro: "10",         enterprise: "Unlimited" },
  { label: "Messages / month",    starter: "10,000",    pro: "100,000",    enterprise: "Unlimited" },
  { label: "Conversations stored",starter: "30 days",   pro: "1 year",     enterprise: "Unlimited" },

  { section: "Content Sources" },
  { label: "Website crawling",    starter: true,  pro: true,  enterprise: true  },
  { label: "PDF & file upload",   starter: true,  pro: true,  enterprise: true  },
  { label: "Custom text content", starter: false, pro: true,  enterprise: true  },
  { label: "Notion sync",         starter: false, pro: true,  enterprise: true  },
  { label: "YouTube training",    starter: false, pro: true,  enterprise: true  },
  { label: "Confluence",          starter: false, pro: true,  enterprise: true  },
  { label: "Zendesk",             starter: false, pro: true,  enterprise: true  },

  { section: "Chatbot Features" },
  { label: "Lead capture",        starter: false, pro: true,  enterprise: true  },
  { label: "CRM sync",            starter: false, pro: true,  enterprise: true  },
  { label: "15+ languages",       starter: false, pro: true,  enterprise: true  },
  { label: "Real-time monitoring",starter: false, pro: true,  enterprise: true  },
  { label: "Inbox & conversation history", starter: false, pro: true, enterprise: true },
  { label: "Custom appearance",   starter: true,  pro: true,  enterprise: true  },
  { label: "Embed on any website",starter: true,  pro: true,  enterprise: true  },

  { section: "Analytics" },
  { label: "Analytics dashboard", starter: "Basic", pro: "Advanced", enterprise: "Advanced" },
  { label: "Export data",         starter: false,   pro: true,        enterprise: true       },

  { section: "Advanced" },
  { label: "White-label branding",starter: false, pro: false, enterprise: true },
  { label: "SSO & audit logs",    starter: false, pro: false, enterprise: true },
  { label: "Dedicated onboarding",starter: false, pro: false, enterprise: true },
  { label: "Custom integrations", starter: false, pro: false, enterprise: true },

  { section: "Support" },
  { label: "Support",             starter: "Email", pro: "Priority email", enterprise: "24/7 dedicated" },
  { label: "SLA guarantee",       starter: false,   pro: false,            enterprise: true               },
];

function CellValue({ value }) {
  if (value === true)  return <FiCheck size={15} className="text-primary mx-auto" />;
  if (value === false) return <FiMinus size={14} className="text-gray-300 mx-auto" />;
  return <span className="text-xs font-medium text-gray-700">{value}</span>;
}

export default function PricingTab() {
  const [annual, setAnnual] = useState(true);

  return (
    <div className="space-y-8">

      {/* ── Current plan banner ── */}
      <div className="bg-gradient-to-r from-primary/5 to-indigo-50 border border-primary/15 rounded-2xl px-5 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-indigo-500 flex items-center justify-center shadow-sm shadow-primary/30">
            <FiZap size={16} className="text-white" />
          </div>
          <div>
            <p className="text-sm font-bold text-gray-900">You're on the <span className="text-primary">Pro Plan</span></p>
            <p className="text-xs text-gray-500 mt-0.5">8,420 of 100,000 messages used this month · 3 of 10 chatbots created</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-28 h-1.5 bg-primary/15 rounded-full overflow-hidden">
            <div className="h-full w-[8%] bg-primary rounded-full" />
          </div>
          <span className="text-xs text-gray-400 font-medium">8%</span>
        </div>
      </div>

      {/* ── Billing toggle ── */}
      <div className="flex items-center justify-center gap-3">
        <span className={`text-sm font-medium ${!annual ? "text-gray-900" : "text-gray-400"}`}>Monthly</span>
        <button
          onClick={() => setAnnual((v) => !v)}
          className={`relative w-11 h-6 rounded-full transition-colors ${annual ? "bg-primary" : "bg-gray-200"}`}
        >
          <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${annual ? "translate-x-5" : ""}`} />
        </button>
        <span className={`text-sm font-medium ${annual ? "text-gray-900" : "text-gray-400"}`}>
          Annual
        </span>
        {annual && (
          <span className="text-[11px] font-bold bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
            Save 20%
          </span>
        )}
      </div>

      {/* ── Plan cards ── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {PLANS.map((plan) => (
          <div
            key={plan.key}
            className={`relative bg-white rounded-2xl border overflow-hidden flex flex-col transition-shadow ${
              plan.current
                ? "border-primary shadow-lg shadow-primary/10"
                : "border-gray-200 shadow-sm hover:shadow-md"
            }`}
          >
            {/* Most popular badge */}
            {plan.current && (
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary to-indigo-500" />
            )}

            {/* Card header */}
            <div className={`bg-gradient-to-br ${plan.color} px-5 pt-5 pb-8`}>
              {plan.current && (
                <span className="inline-block text-[10px] font-bold bg-white/20 text-white px-2.5 py-0.5 rounded-full mb-3 backdrop-blur-sm">
                  Most Popular
                </span>
              )}
              <p className="text-lg font-bold text-white">{plan.name}</p>
              <p className="text-xs text-white/70 mt-0.5">{plan.tagline}</p>
            </div>

            {/* Price section — pulled over gradient */}
            <div className="px-5 -mt-5 flex flex-col flex-1">
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm px-4 py-3 mb-5">
                {plan.monthlyPrice ? (
                  <div className="flex items-end gap-1">
                    <span className="text-3xl font-bold text-gray-900">
                      ${annual ? plan.yearlyPrice : plan.monthlyPrice}
                    </span>
                    <span className="text-sm text-gray-400 mb-1">/ mo</span>
                    {annual && (
                      <span className="text-[11px] text-green-600 font-semibold ml-1 mb-1">
                        ${plan.yearlyTotal}/yr
                      </span>
                    )}
                  </div>
                ) : (
                  <p className="text-3xl font-bold text-gray-900">Custom</p>
                )}
                <p className="text-[11px] text-gray-400 mt-0.5">
                  {plan.monthlyPrice ? (annual ? "Billed annually" : "Billed monthly") : "Volume pricing & SLA"}
                </p>
              </div>

              {/* Limits highlight */}
              <div className="space-y-2 mb-5">
                {Object.values(plan.limits).map((val) => (
                  <div key={val} className="flex items-center gap-2 text-sm text-gray-700">
                    <FiCheck size={13} className="text-primary shrink-0" />
                    <span className="font-medium">{val}</span>
                  </div>
                ))}
              </div>

              {/* Spacer */}
              <div className="flex-1" />

              {/* CTA */}
              <div className="pb-5">
                <button
                  className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition ${
                    plan.current
                      ? "bg-primary text-white shadow-sm shadow-primary/30 cursor-default opacity-80"
                      : plan.key === "enterprise"
                      ? "border-2 border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50"
                      : "border-2 border-primary/20 text-primary hover:bg-primary-light hover:border-primary/40"
                  }`}
                >
                  {plan.cta}
                  {!plan.current && <FiArrowRight size={13} />}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Feature comparison table ── */}
      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
        <div className="px-6 py-4 border-b bg-gray-50/70">
          <h3 className="text-sm font-bold text-gray-900">Full feature comparison</h3>
          <p className="text-xs text-gray-500 mt-0.5">See everything included in each plan</p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50/50">
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 w-1/2">Feature</th>
                {PLANS.map((p) => (
                  <th key={p.key} className={`px-4 py-3 text-center text-xs font-bold w-[16%] ${p.current ? "text-primary" : "text-gray-600"}`}>
                    {p.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {FEATURES.map((row, i) => {
                if (row.section) {
                  return (
                    <tr key={i} className="bg-gray-50/80">
                      <td colSpan={4} className="px-6 py-2.5">
                        <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">{row.section}</span>
                      </td>
                    </tr>
                  );
                }
                return (
                  <tr key={i} className="border-t border-gray-50 hover:bg-gray-50/50 transition-colors">
                    <td className="px-6 py-3 text-xs text-gray-600 font-medium">{row.label}</td>
                    <td className="px-4 py-3 text-center"><CellValue value={row.starter} /></td>
                    <td className={`px-4 py-3 text-center ${row.pro && row.pro !== false ? "bg-primary/[0.02]" : ""}`}>
                      <CellValue value={row.pro} />
                    </td>
                    <td className="px-4 py-3 text-center"><CellValue value={row.enterprise} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Enterprise CTA ── */}
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl px-8 py-7 flex items-center justify-between">
        <div>
          <p className="text-lg font-bold text-white">Need more than Pro?</p>
          <p className="text-sm text-gray-400 mt-1">
            Get custom limits, white-label branding, SSO, SLA, and a dedicated account manager.
          </p>
        </div>
        <button className="shrink-0 flex items-center gap-2 px-6 py-2.5 bg-white text-gray-900 rounded-xl text-sm font-bold hover:bg-gray-100 transition">
          Contact Sales <FiArrowRight size={14} />
        </button>
      </div>

    </div>
  );
}
